const FIRST_PANEL = 1
const SECOND_PANEL = 2
const BOTH_PANELS = FIRST_PANEL | SECOND_PANEL

const addState = (states, count, usedPanels) => {
  if (!states.has(count)) states.set(count, new Set())
  states.get(count).add(usedPanels)
}

/**
 * Find a minimum-gap split without enumerating category permutations.
 * Each layer records reachable participant totals and which panels contain a
 * category, so even zero-participant categories produce two nonempty groups.
 * Random backtracking keeps alternative optimal assignments selectable.
 * Time and space are O(categories.length * (total participants + 1)).
 */
export const randomCategorySplit = (categories, random = Math.random) => {
  if (categories.length < 2) {
    return categories.map(category => ({ count: category.count, categories: [category] }))
  }

  const layers = [new Map([[0, new Set([0])]])]
  let total = 0
  categories.forEach(category => {
    const next = new Map()
    layers[layers.length - 1].forEach((masks, count) => {
      masks.forEach(usedPanels => {
        addState(next, count + category.count, usedPanels | FIRST_PANEL)
        addState(next, count, usedPanels | SECOND_PANEL)
      })
    })
    layers.push(next)
    total += category.count
  })

  // Keep the heavier panel first, matching the repeat-order block allocation.
  let firstCount = total
  layers[categories.length].forEach((masks, count) => {
    if (masks.has(BOTH_PANELS) && count >= total / 2 && count < firstCount) {
      firstCount = count
    }
  })

  const groups = [
    { count: firstCount, categories: [] },
    { count: total - firstCount, categories: [] },
  ]
  let remaining = firstCount
  let usedPanels = BOTH_PANELS

  for (let index = categories.length - 1; index >= 0; index--) {
    const category = categories[index]
    const previous = layers[index]
    const choices = []
    for (let mask = 0; mask <= BOTH_PANELS; mask++) {
      if ((mask | FIRST_PANEL) === usedPanels
        && previous.get(remaining - category.count)?.has(mask)) {
        choices.push({ panel: 0, remaining: remaining - category.count, usedPanels: mask })
      }
      if ((mask | SECOND_PANEL) === usedPanels && previous.get(remaining)?.has(mask)) {
        choices.push({ panel: 1, remaining, usedPanels: mask })
      }
    }
    const choice = choices[Math.floor(random() * choices.length)]
    groups[choice.panel].categories.push(category)
    remaining = choice.remaining
    usedPanels = choice.usedPanels
  }

  groups.forEach(group => group.categories.reverse())
  return groups
}
